/**
 * Kleine Bausteine für die Oberfläche.
 *
 * Alles wird über DOM-Knoten gebaut, nicht über innerHTML mit eingesetzten
 * Werten: Kontaktdaten, Dateinamen und Fehlermeldungen sind fremder Inhalt.
 * `el()` nimmt Text immer als textContent - damit ist Markup-Einschleusung
 * strukturell ausgeschlossen, nicht nur "gerade nicht der Fall".
 */

/**
 * Erzeugt ein Element.
 *
 * @param {string} tag  z.B. 'div', 'button.haupt', 'p#hinweis'
 * @param {object|string|Node|Array<string|Node|null|undefined>} [eigenschaftenOderKinder]
 * @param {Array<string|Node|null|undefined>|string|Node} [kinder]
 * @returns {HTMLElement}
 */
export function el(tag, eigenschaftenOderKinder, kinder) {
  const [name, ...klassen] = tag.split('.');
  const [echterName, id] = (name ?? 'div').split('#');
  const knoten = document.createElement(echterName || 'div');
  if (id) knoten.id = id;
  if (klassen.length) knoten.className = klassen.join(' ');

  let eigenschaften = /** @type {Record<string, any>} */ ({});
  let inhalt = kinder;

  const istInhalt =
    typeof eigenschaftenOderKinder === 'string' ||
    eigenschaftenOderKinder instanceof Node ||
    Array.isArray(eigenschaftenOderKinder);

  if (istInhalt) {
    inhalt = /** @type {any} */ (eigenschaftenOderKinder);
  } else if (eigenschaftenOderKinder) {
    eigenschaften = /** @type {Record<string, any>} */ (eigenschaftenOderKinder);
  }

  for (const [schluessel, wert] of Object.entries(eigenschaften)) {
    if (wert === undefined || wert === null || wert === false) continue;
    if (schluessel === 'text') {
      // Es gibt bewusst KEINE html-Option. Alles, was hier eingesetzt wird,
      // stammt aus Kontaktdaten, Importdateien oder Fehlermeldungen - also
      // aus fremder Hand. Die Vorschau der Nachricht läuft über ein iframe
      // mit srcdoc, nicht über eingesetztes Markup.
      knoten.textContent = String(wert);
    } else if (schluessel.startsWith('on') && typeof wert === 'function') {
      knoten.addEventListener(schluessel.slice(2).toLowerCase(), wert);
    } else if (schluessel === 'dataset') {
      Object.assign(knoten.dataset, wert);
    } else {
      knoten.setAttribute(schluessel, wert === true ? '' : String(wert));
    }
  }

  const liste = Array.isArray(inhalt) ? inhalt : inhalt !== undefined ? [inhalt] : [];
  for (const kind of liste) {
    if (kind === null || kind === undefined || kind === false) continue;
    knoten.append(typeof kind === 'string' ? document.createTextNode(kind) : kind);
  }
  return knoten;
}

/**
 * Leert einen Container und füllt ihn neu.
 * @param {HTMLElement} ziel
 * @param {...(Node|string|null|undefined)} inhalt
 */
export function ersetzen(ziel, ...inhalt) {
  ziel.replaceChildren(...inhalt.filter((k) => k !== null && k !== undefined));
}

/**
 * Karte mit Titel und Inhalt - das Grundelement der Oberfläche.
 *
 * @param {object} optionen
 * @param {string} [optionen.titel]
 * @param {string} [optionen.hinweis]
 * @param {Node|Node[]} optionen.inhalt
 * @param {Node} [optionen.aktion]
 * @param {boolean} [optionen.betont] Hebt die Karte als nächsten Schritt hervor.
 * @returns {HTMLElement}
 */
export function karte({ titel, hinweis, inhalt, aktion, betont }) {
  return el(`section.karte${betont ? '.betont' : ''}`, [
    titel || aktion
      ? el('header.karte-kopf', [
          el('div', [
            titel ? el('h2', { text: titel }) : null,
            hinweis ? el('p.hinweis', { text: hinweis }) : null,
          ]),
          aktion ?? null,
        ])
      : null,
    el('div.karte-inhalt', Array.isArray(inhalt) ? inhalt : [inhalt]),
  ]);
}

/**
 * @param {string} beschriftung
 * @param {object} [optionen]
 * @param {'haupt'|'still'|'gefahr'} [optionen.art]
 * @param {() => void} [optionen.beiKlick]
 * @param {boolean} [optionen.deaktiviert]
 * @param {string} [optionen.titel]
 * @param {string} [optionen.klasse] Zusätzliche Klasse, etwa für Sichtbarkeit
 *   je nach Bildschirmbreite.
 * @returns {HTMLButtonElement}
 */
export function knopf(beschriftung, optionen = {}) {
  const k = /** @type {HTMLButtonElement} */ (
    el(
      `button.knopf${optionen.art ? `.${optionen.art}` : ''}${
        optionen.klasse ? `.${optionen.klasse}` : ''
      }`,
      {
        type: 'button',
        text: beschriftung,
        ...(optionen.titel ? { title: optionen.titel } : {}),
      },
    )
  );
  if (optionen.deaktiviert) k.disabled = true;
  if (optionen.beiKlick) k.addEventListener('click', optionen.beiKlick);
  return k;
}

/**
 * Beschriftetes Eingabefeld.
 *
 * @param {object} optionen
 * @param {string} optionen.beschriftung
 * @param {string} [optionen.hinweis]
 * @param {string} [optionen.wert]
 * @param {string} [optionen.platzhalter]
 * @param {'text'|'email'|'textarea'|'select'} [optionen.art]
 * @param {{ wert: string, text: string }[]} [optionen.auswahl]
 * @param {(wert: string) => void} [optionen.beiAenderung]
 * @returns {{ feld: HTMLElement, eingabe: HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement }}
 */
export function eingabefeld(optionen) {
  const kennung = `feld-${Math.random().toString(36).slice(2, 9)}`;
  let eingabe;

  if (optionen.art === 'textarea') {
    eingabe = /** @type {HTMLTextAreaElement} */ (
      el('textarea', { id: kennung, rows: 8, placeholder: optionen.platzhalter ?? '' })
    );
    eingabe.value = optionen.wert ?? '';
  } else if (optionen.art === 'select') {
    eingabe = /** @type {HTMLSelectElement} */ (el('select', { id: kennung }));
    for (const eintrag of optionen.auswahl ?? []) {
      const option = /** @type {HTMLOptionElement} */ (
        el('option', { value: eintrag.wert, text: eintrag.text })
      );
      eingabe.append(option);
    }
    if (optionen.wert !== undefined) eingabe.value = optionen.wert;
  } else {
    eingabe = /** @type {HTMLInputElement} */ (
      el('input', {
        id: kennung,
        type: optionen.art ?? 'text',
        placeholder: optionen.platzhalter ?? '',
      })
    );
    eingabe.value = optionen.wert ?? '';
  }

  if (optionen.beiAenderung) {
    eingabe.addEventListener('input', () => optionen.beiAenderung?.(eingabe.value));
  }

  const feld = el('div.feld', [
    el('label', { for: kennung, text: optionen.beschriftung }),
    eingabe,
    optionen.hinweis ? el('p.feld-hinweis', { text: optionen.hinweis }) : null,
  ]);
  return { feld, eingabe };
}

/**
 * Meldungsstreifen.
 * @param {'erfolg'|'warnung'|'fehler'|'info'} art
 * @param {string} text
 * @param {Node} [zusatz]
 * @returns {HTMLElement}
 */
export function meldung(art, text, zusatz) {
  return el(`div.meldung.${art}`, [el('span.meldung-text', { text }), zusatz ?? null]);
}

/**
 * Kennzeichen für einen Zustand.
 * @param {string} text
 * @param {'neutral'|'gruen'|'blau'|'rot'|'gelb'} [farbe]
 * @returns {HTMLElement}
 */
export function kennzeichen(text, farbe = 'neutral') {
  return el(`span.kennzeichen.${farbe}`, { text });
}

/**
 * Tabelle aus Spaltendefinitionen und Zeilen.
 *
 * @template T
 * @param {object} optionen
 * @param {{ titel: string, zelle: (zeile: T) => Node|string, schmal?: boolean }[]} optionen.spalten
 * @param {T[]} optionen.zeilen
 * @param {string} [optionen.leerText]
 * @returns {HTMLElement}
 */
export function tabelle({ spalten, zeilen, leerText }) {
  if (!zeilen.length) {
    return el('p.leer', { text: leerText ?? 'Noch nichts vorhanden.' });
  }
  return el('div.tabelle-rahmen', [
    el('table.tabelle', [
      el('thead', [
        el(
          'tr',
          spalten.map((s) => el(`th${s.schmal ? '.schmal' : ''}`, { text: s.titel })),
        ),
      ]),
      el(
        'tbody',
        zeilen.map((zeile) =>
          el(
            'tr',
            spalten.map((s) => {
              const wert = s.zelle(zeile);
              return el('td', typeof wert === 'string' ? { text: wert } : [wert]);
            }),
          ),
        ),
      ),
    ]),
  ]);
}

/**
 * Ladeanzeige.
 * @param {string} [text]
 * @returns {HTMLElement}
 */
export function laedt(text = 'Wird geladen …') {
  return el('div.laedt', [el('span.spinner'), el('span', { text })]);
}

/**
 * Schrittanzeige für den geführten Ablauf.
 *
 * @param {{ titel: string }[]} schritte
 * @param {number} aktiv  0-basiert
 * @returns {HTMLElement}
 */
export function schrittanzeige(schritte, aktiv) {
  return el(
    'ol.schritte',
    schritte.map((schritt, index) => {
      const zustand = index < aktiv ? 'erledigt' : index === aktiv ? 'aktiv' : 'offen';
      return el(`li.schritt.${zustand}`, [
        el('span.schritt-nummer', { text: index < aktiv ? '✓' : String(index + 1) }),
        el('span.schritt-titel', { text: schritt.titel }),
      ]);
    }),
  );
}

/**
 * Modaler Dialog. Gibt einen Schließer zurück.
 *
 * @param {object} optionen
 * @param {string} optionen.titel
 * @param {Node|Node[]} optionen.inhalt
 * @param {Node[]} [optionen.aktionen]
 * @param {boolean} [optionen.breit] Für Inhalte, die Fläche brauchen —
 *   etwa eine vollständige Nachrichtenvorschau.
 * @returns {() => void}
 */
export function dialog({ titel, inhalt, aktionen, breit = false }) {
  const schliessen = () => huelle.remove();

  const huelle = el('div.dialog-huelle', {
    onclick: (/** @type {MouseEvent} */ ereignis) => {
      if (ereignis.target === huelle) schliessen();
    },
  });

  huelle.append(
    el(`div.dialog${breit ? '.breit' : ''}`, { role: 'dialog', 'aria-modal': 'true' }, [
      el('header.dialog-kopf', [
        el('h2', { text: titel }),
        el('button.dialog-zu', { type: 'button', 'aria-label': 'Schließen', text: '×', onclick: schliessen }),
      ]),
      el('div.dialog-inhalt', Array.isArray(inhalt) ? inhalt : [inhalt]),
      aktionen?.length ? el('footer.dialog-fuss', aktionen) : null,
    ]),
  );

  document.addEventListener(
    'keydown',
    (ereignis) => {
      if (ereignis.key === 'Escape') schliessen();
    },
    { once: true },
  );

  document.body.append(huelle);
  return schliessen;
}

/**
 * Formatiert einen Zeitstempel für die Anzeige.
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export function zeitpunkt(iso) {
  if (!iso) return '—';
  const datum = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(datum.getTime())) return String(iso);
  return datum.toLocaleString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
